import React, { useEffect, useRef, useState } from "react";

import Select from "react-dropdown-select";
import { AiOutlineClose } from "react-icons/ai";
import axiosInstance from "../services/axiosInstance";

const EditStandardNotePopup = ({
  getStandardNoteList,
  selectedStandardNote,
  setSelectedStandardNote,
  openPopUp,
  closePopUp,
  standardNoteList,
}) => {
  const [dentistCodes, setDentistCodes] = useState([]);
  const [workTypeCodes, setWorkTypeCodes] = useState([]);
  const [errors, setErrors] = useState({
    dentistCode: "",
    workTypeCode: "",
    standardNote: "",
  });

  // Cache for API data
  const cachedData = useRef({ dentistDetails: null, workTypeDetails: null });

  const handleClosePopUp = (e) => {
    setErrors({
      dentistCode: "",
      workTypeCode: "",
      standardNote: "",
    });
    closePopUp();
  };

  const validateUniqueCombination = () => {
    // Check if the combination exists in any other record
    const existingCombination = standardNoteList?.some(
      (note) =>
        note.dentistId === selectedStandardNote?.dentistId &&
        note.workTypeId === selectedStandardNote?.workTypeId &&
        note.dentistWorkTypeNoteId !==
          selectedStandardNote?.dentistWorkTypeNoteId
    );

    // Return true if no existing combination found
    return !existingCombination;
  };

  const saveStandardNote = async () => {
    // Reset any previous errors
    // setErrors({
    //   dentistCode: "",
    //   workTypeCode: "",
    //   standardNote: ""
    // });

    // Basic validation
    if (
      !selectedStandardNote?.dentistCode ||
      !selectedStandardNote?.workTypeCode
    ) {
      setErrors((prevState) => ({
        ...prevState,
        //generalValidation: "Please select both Dentist Code and Work Type Code",
      }));
      return;
    }

    // Check for unique combination
    const isUnique = validateUniqueCombination();
    if (!isUnique) {
      setErrors((prevState) => ({
        ...prevState,
        generalValidation: "This combination of Dentist Code and Work Type Code already exists",
      }));
      return;
    }

    // If validation passes, proceed with saving
    const response = await axiosInstance.post(
      "/api/StandardNotes/updateStandardNotes",
      selectedStandardNote
    );

    if (response.isSuccess) {
      getStandardNoteList();
      setErrors({
        dentistCode: "",
        workTypeCode: "",
        standardNote: ""
      });
      closePopUp();
    }
  };

  const getDentistCodeAndWorkTypeCode = async () => {
    if (cachedData.current.dentistCodes && cachedData.current.workTypeCodes) {
      setDentistCodes(cachedData.current.dentistDetails);
      setWorkTypeCodes(cachedData.current.workTypeDetails);
      return;
    }

    const response = await axiosInstance.get(
      "/api/StandardNotes/getDentistAndWorkTypeCode"
    );

    if (response.isSuccess) {
      const { dentistDetails, workTypeDetails } = response.standadNoteList;

      // Cache the data
      cachedData.current.dentistDetails = dentistDetails;
      cachedData.current.workTypeDetails = workTypeDetails;

      // Update state
      setDentistCodes(
        dentistDetails.map((x) => ({
          label: x.dentistCode,
          value: x.dentistId,
        }))
      );
      setWorkTypeCodes(
        workTypeDetails
          .sort((a, b) =>
            String(a.workTypeCode).localeCompare(String(b.workTypeCode))
          ) // Sort to Asc
          .map((x) => ({
            label: x.workTypeCode,
            value: x.workTypeId,
          }))
      );
    }
  };

  useEffect(() => {
    getDentistCodeAndWorkTypeCode();
  }, []);

  if (openPopUp !== true) return null;

  return (
    <div
      id="ModelContainer"
      className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm z-[10000] overflow-visible"
    >
      <div className="flex justify-center items-center py-10">
        <div className="relative bg-white w-5/6 md:w-[730px] mx-auto rounded-[30px] p-20 pt-14 shadow-[0_0_7px_rgba(92,92,92,0.35)] drop-shadow-[0_0_4px_rgba(178,178,178,0.2)">
          <a
            className="absolute right-8 top-8 text-4xl text-gray-500 pointer"
            id="closePopup"
            onClick={handleClosePopUp}
          >
            <AiOutlineClose />
          </a>
          <div className="w-full">
            <p className="font-interBold text-blue-600 py-7 text-3xl">
              Details bearbeiten
            </p>
            <div>
              <div className="flex items-center mb-5">
                <label className="block text-gray-500 w-40 flex-shrink-0 text-2xl font-interBold mb-2">
                  Dentist Code <sup className="text-red">*</sup>
                </label>
                <div className="flex-grow">
                  <Select
                    className="appearance-none h-12 !border-gray !leading-normal !text-2xl !w-full bg-white !rounded-lg !pl-5 !py-3 !px-2 !text-gray-600 !placeholder:text-gray-600 !hover:border-blue-600 !focus:border-blue-600 !focus:outline-none shadow-none"
                    values={dentistCodes.filter(
                      (option) =>
                        option.label === selectedStandardNote?.dentistCode
                    )}
                    options={dentistCodes}
                    onChange={(selectedOptions) => {
                      if (selectedOptions && selectedOptions.length > 0) {
                        const selectedOption = selectedOptions[0];
                        if (selectedOption?.value) {
                          setSelectedStandardNote((prev) => ({
                            ...prev,
                            dentistId: selectedOption.value,
                            dentistCode: selectedOption.label,
                          }));
                          setErrors((prev) => ({ ...prev, dentistCode: "" }));
                        } else {
                          setSelectedStandardNote((prev) => ({
                            ...prev,
                            dentistId: null,
                            dentistCode: null,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            dentistCode: "Please select a valid dentist code",
                          }));
                        }
                      } else {
                        setSelectedStandardNote((prev) => ({
                          ...prev,
                          dentistId: null,
                          dentistCode: null,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          dentistCode: "Dentist code is required",
                          generalValidation: "",
                        }));
                      }
                    }}
                    labelField="label"
                    valueField="value"
                    placeholder="Select a dentist code"
                    required={true}
                    dropdownHandle={false}
                    searchable={true}
                    dropdownPosition="auto"
                    noDataLabel="No dentist codes available"
                  />
                  {errors.dentistCode && (
                    <p className="text-red text-sm mt-1">
                      {errors.dentistCode}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center mb-5">
                <label className="block text-gray-500 w-40 flex-shrink-0 text-2xl font-interBold mb-2">
                  Work Type Code <sup className="text-red">*</sup>
                </label>
                <div className="flex-grow">
                  <Select
                    className="appearance-none h-12 !border-gray !leading-normal !text-2xl !w-full bg-white !rounded-lg !pl-5 !py-3 !px-2 !text-gray-600 !placeholder:text-gray-600 !hover:border-blue-600 !focus:border-blue-600 !focus:outline-none shadow-none"
                    values={workTypeCodes.filter(
                      (option) =>
                        option.label === selectedStandardNote?.workTypeCode
                    )}
                    options={workTypeCodes}
                    onChange={(selectedOptions) => {
                      if (selectedOptions && selectedOptions.length > 0) {
                        const selectedOption = selectedOptions[0];
                        if (selectedOption?.value) {
                          setSelectedStandardNote((prev) => ({
                            ...prev,
                            workTypeId: selectedOption.value,
                            workTypeCode: selectedOption.label,
                          }));
                          setErrors((prev) => ({ ...prev, workTypeCode: "" }));
                        } else {
                          setSelectedStandardNote((prev) => ({
                            ...prev,
                            workTypeId: null,
                            workTypeCode: null,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            workTypeCode:
                              "Please select a valid work type code",
                          }));
                        }
                      } else {
                        setSelectedStandardNote((prev) => ({
                          ...prev,
                          workTypeId: null,
                          workTypeCode: null,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          workTypeCode: "Work type code is required",
                          generalValidation: "",
                        }));
                      }
                    }}
                    labelField="label"
                    valueField="value"
                    placeholder="Select a worktype code"
                    required={true}
                    dropdownHandle={false}
                    searchable={true}
                    dropdownPosition="auto"
                    noDataLabel="No work type codes available"
                  />
                  {errors.workTypeCode && (
                    <p className="text-red text-sm mt-1">
                      {errors.workTypeCode}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center mb-5">
                <label className="block text-gray-500 w-40 flex-shrink-0 text-2xl font-interBold mb-2">
                  Bitte Beachten
                </label>
                <input
                  className="appearance-none border h-12 border-gray leading-relaxed text-2xl w-full bg-white rounded-lg !pl-5 py-3 px-5 text-gray-600 placeholder:text-gray-600 hover:border-blue-600 focus:border-blue-600 focus:outline-none shadow-none"
                  type="text"
                  placeholder="Bitte Beachten"
                  value={selectedStandardNote?.standard_Notes}
                  onChange={(e) => {
                    setSelectedStandardNote((prevUser) => ({
                      ...prevUser,
                      standard_Notes: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            {errors.generalValidation && (
              <p className="text-red text-sm mt-1">
                {errors.generalValidation}
              </p>
            )}

            <div className="mt-5 flex items-center justify-end gap-5">
              <button
                className="px-5 py-2 border uppercase border-blue-500 text-blue font-robotoBold rounded-lg hover:bg-blue-800 hover:border-blue-800 hover:text-white transition duration-300"
                onClick={handleClosePopUp}
              >
                Abbrechen
              </button>
              <button
                className="bg-blue uppercase border border-blue hover:bg-blue-800 hover:border-blue-800 text-white font-robotoBold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in leading-relaxed"
                type="button"
                onClick={saveStandardNote}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStandardNotePopup;
